import { useMemo } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { MetricResult } from '../api/metrics';
import type { ChartType } from '../dashboard/spec';
import { AutoSize } from './AutoSize';
import { SEQUENTIAL, STATUS } from './palette';
import { ChartTooltip, money } from './tooltip';
import { useTheme } from '../theme';

interface MetricChartProps {
  result: MetricResult;
  chart: ChartType;
  limit?: number | null;
}

const shortDay = (iso: string): string => {
  const date = new Date(iso);
  return `${date.getUTCDate()}/${date.getUTCMonth() + 1}`;
};

export const MetricChart = ({ result, chart, limit }: MetricChartProps) => {
  const { theme } = useTheme();
  const { ink } = theme;
  const axisProps = {
    tick: { fill: ink.secondary, fontSize: 11 },
    axisLine: false,
    tickLine: false,
  } as const;
  const format = result.unit === 'gbp' ? money : (value: number) => value.toLocaleString('en-GB');
  const data = useMemo(
    () => (limit ? result.points.slice(0, limit) : result.points),
    [result.points, limit],
  );
  const max = Math.max(1, ...data.map(({ value }) => value));
  const isDaily = result.group_by === 'day';
  const labelFormatter = (value: number) =>
    value > 0 ? format(value) : result.unit === 'gbp' ? 'not set' : '0';

  if (isDaily && (chart === 'line' || chart === 'area')) {
    const Chart = chart === 'area' ? AreaChart : LineChart;
    return (
      <AutoSize>
        {({ width, height }) => (
          <Chart
            width={width}
            height={height}
            data={data}
            margin={{ top: 12, right: 12, bottom: 0, left: -20 }}
          >
            <CartesianGrid vertical={false} stroke={ink.grid} />
            <XAxis dataKey="key" {...axisProps} tickFormatter={shortDay} minTickGap={24} />
            <YAxis {...axisProps} allowDecimals={false} />
            <Tooltip
              content={<ChartTooltip />}
              cursor={{ stroke: ink.secondary, strokeDasharray: '3 3' }}
            />
            <Legend
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: 12, color: ink.secondary }}
            />
            {chart === 'area' ? (
              <Area
                isAnimationActive
                animationDuration={500}
                animationEasing="ease-out"
                type="monotone"
                dataKey="series.publishes"
                name="Publishes"
                stroke={ink.series[0]}
                fill={ink.series[0]}
                fillOpacity={0.15}
                strokeWidth={2}
              />
            ) : (
              <Line
                isAnimationActive
                animationDuration={500}
                animationEasing="ease-out"
                type="monotone"
                dataKey="series.publishes"
                name="Publishes"
                stroke={ink.series[0]}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 5, stroke: ink.surface, strokeWidth: 2 }}
              />
            )}
            {chart === 'area' ? (
              <Area
                isAnimationActive
                animationDuration={500}
                animationEasing="ease-out"
                type="monotone"
                dataKey="series.failures"
                name="Failures"
                stroke={STATUS.error}
                fill={STATUS.error}
                fillOpacity={0.12}
                strokeWidth={2}
              />
            ) : (
              <Line
                isAnimationActive
                animationDuration={500}
                animationEasing="ease-out"
                type="monotone"
                dataKey="series.failures"
                name="Failures"
                stroke={STATUS.error}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 5, stroke: ink.surface, strokeWidth: 2 }}
              />
            )}
          </Chart>
        )}
      </AutoSize>
    );
  }

  if (chart === 'bar') {
    return (
      <AutoSize>
        {({ width, height }) => (
          <BarChart
            width={width}
            height={height}
            data={data}
            margin={{ top: 16, right: 8, bottom: 0, left: -12 }}
            barCategoryGap={10}
          >
            <CartesianGrid vertical={false} stroke={ink.grid} />
            <XAxis
              dataKey="label"
              {...axisProps}
              interval={isDaily ? 'preserveStartEnd' : 0}
              tickFormatter={isDaily ? shortDay : (value: string) => value.split(' ')[0]}
              minTickGap={isDaily ? 24 : 0}
            />
            <YAxis {...axisProps} tickFormatter={(value) => format(Number(value))} />
            <Tooltip cursor={{ fill: ink.grid }} content={<ChartTooltip format={format} />} />
            <Bar
              isAnimationActive
              animationDuration={500}
              animationEasing="ease-out"
              dataKey="value"
              name={result.metric}
              fill={ink.series[0]}
              radius={[4, 4, 0, 0]}
              maxBarSize={36}
              label={
                isDaily
                  ? undefined
                  : {
                      position: 'top',
                      fill: ink.secondary,
                      fontSize: 11,
                      formatter: labelFormatter,
                    }
              }
            />
          </BarChart>
        )}
      </AutoSize>
    );
  }

  return (
    <AutoSize>
      {({ width, height }) => (
        <BarChart
          width={width}
          height={height}
          data={data}
          layout="vertical"
          margin={{ top: 4, right: 56, bottom: 4, left: 4 }}
          barCategoryGap={6}
        >
          <CartesianGrid horizontal={false} stroke={ink.grid} />
          <XAxis
            type="number"
            {...axisProps}
            tickFormatter={(value) => format(Number(value))}
            allowDecimals={false}
          />
          <YAxis type="category" dataKey="label" width={120} {...axisProps} />
          <Tooltip cursor={{ fill: ink.grid }} content={<ChartTooltip format={format} />} />
          <Bar
            isAnimationActive
            animationDuration={500}
            animationEasing="ease-out"
            dataKey="value"
            name={result.metric}
            radius={[0, 4, 4, 0]}
            maxBarSize={18}
            label={{
              position: 'right',
              fill: ink.secondary,
              fontSize: 11,
              formatter: labelFormatter,
            }}
          >
            {data.map(({ key, value }) => (
              <Cell
                key={key}
                fill={
                  result.unit === 'gbp'
                    ? SEQUENTIAL[Math.min(4, Math.max(1, Math.ceil((value / max) * 4)))]
                    : ink.series[0]
                }
              />
            ))}
          </Bar>
        </BarChart>
      )}
    </AutoSize>
  );
};
